import {
  Outlet,
  createFileRoute,
  redirect,
  useRouterState,
  Link,
} from '@tanstack/react-router';
import { fetchApi } from '@/lib/api'; // <-- Import fetchApi
import { Skeleton } from '@/components/ui/skeleton'; // <-- Import Skeleton for loading
import {
  LayoutDashboard,
  CalendarDays,
  Settings,
  LogOut,
} from 'lucide-react'; // Icons for sidebar

// Helper function to check auth status
// Uses fetchApi now
async function checkAuthStatus() {
  try {
    const response = await fetchApi('/api/auth/status'); // <-- Use fetchApi
    if (response.ok) {
      const data = await response.json();
      return data.authenticated === true;
    } else {
      // Log non-ok status but return false
      console.log(`Auth status check failed with status: ${response.status}`);
      return false;
    }
  } catch (error) {
    console.error('Auth status check network error:', error);
    return false; // Network or other errors mean not authenticated
  }
}

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location }) => {
    const isAuthenticated = await checkAuthStatus();
    console.log('Authentication status:', isAuthenticated);

    if (!isAuthenticated) {
      throw redirect({
        to: '/login',
        search: {
          // Use the current location to power a redirect after login
          // (Do not use `router.state.resolvedLocation` as it can
          // potentially lag behind the actual current location)
          redirect: location.href,
        },
      });
    }
    // If authenticated, proceed to load the component
    // Data needed by the component can be loaded here if necessary
  },
  component: AuthenticatedLayout,
  // Add a pending component for better UX during auth check
  pendingComponent: LoadingLayout,
});

// Simple loading component
function LoadingLayout() {
    return (
        <div className="p-4 md:p-6">
            <Skeleton className="h-10 w-1/4 mb-6" />
            <Skeleton className="h-64 w-full" />
        </div>
    );
}

// Simple Sidebar Component
function AdminSidebar() {
    // Placeholder for logout functionality
    const handleLogout = async () => {
        console.log('Attempting logout...');
        try {
            await fetchApi('/api/auth/logout', { method: 'POST' });
        } catch (e) {
            console.error('Logout failed:', e);
        }
        // Regardless of API success, redirect to login
        // This might need access to navigate, consider lifting state or using context
        window.location.href = '/login'; // Simple redirect for now
    };

  return (
        <aside className="w-64 bg-card text-card-foreground border-r p-4 flex flex-col">
        <h2 className="text-xl font-semibold mb-6">EBJoy Admin</h2>
            <nav className="flex-grow space-y-2">
              <Link
                to="/dashboard"
                    className="flex items-center p-2 rounded hover:bg-muted [&.active]:bg-primary [&.active]:text-primary-foreground"
                    activeProps={{ className: 'font-bold' }}
              >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                Dashboard
              </Link>
              <Link
                to="/events"
                     className="flex items-center p-2 rounded hover:bg-muted [&.active]:bg-primary [&.active]:text-primary-foreground"
                    activeProps={{ className: 'font-bold' }}
              >
                    <CalendarDays className="mr-2 h-4 w-4" />
                Events
              </Link>
                {/* Add other links like Settings later */}
                 <div
                     className="flex items-center p-2 rounded hover:bg-muted text-muted-foreground cursor-not-allowed"
                 >
                     <Settings className="mr-2 h-4 w-4" />
                     Settings (TBD)
                 </div>
      </nav>
            <div className="mt-auto">
                <button
                    onClick={handleLogout}
                    className="flex items-center w-full p-2 rounded hover:bg-muted text-sm"
                >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                </button>
            </div>
        </aside>
    );
}

function AuthenticatedLayout() {
    // Can potentially get user data from beforeLoad context here if needed
    // const userData = Route.useRouteContext();

    // Use router state to show loading if child route is loading
    const isLoading = useRouterState({ select: (s) => s.status === 'pending' });

  return (
    <div className="flex min-h-screen">
       <AdminSidebar />
       <main className="flex-1 p-4 md:p-6 bg-background">
         {isLoading ? <LoadingLayout /> : <Outlet />}
      </main>
    </div>
  );
}
